import { Component, input, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { ProductionKeyPerformanceIndicatorEvaluationEnum } from 'src/app/enums/production-key-performance-indicator-evaluation.enum';
import {
  ProductionKeyPerformanceIndicatorEvaluation,
  getKpiEvaluationStyle,
} from 'src/app/models/production-key-performance-indicator-evaluation.model';
import { SharePresenceWithColleaguesEvaluatorData } from 'src/app/models/share-presence-with-colleagues-kpi-data.model';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog } from '@angular/material/dialog';
import { SharePresenceWithColleaguesDayDetailsDialogComponent } from './share-presence-with-colleagues-day-details-dialog/share-presence-with-colleagues-day-details-dialog.component';
import { createKpiDetailsDialogConfig } from 'src/app/components/main/employees/performance/my-metrics/kpi-details-dialog/kpi-details-dialog.config';

interface SharePresenceSection {
  name: string;
  done_hours: number;
  expected_hours: number;
}

export interface SharePresenceSectionWithProgress extends SharePresenceSection {
  percentage: number;
  progressBarColor: string;
  isWarning: boolean;
  progressBarTooltip: string | null;
}

@Component({
  selector: 'app-share-presence-with-colleagues-details',
  imports: [CommonModule, DecimalPipe, MatIconModule, MatButtonModule, MatTooltipModule],
  templateUrl: './share-presence-with-colleagues-details.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './share-presence-with-colleagues-details.component.css',
})
export class SharePresenceWithColleaguesDetailsComponent {
  dialog = inject(MatDialog);

  currentMonthEvaluation = input<ProductionKeyPerformanceIndicatorEvaluation | null>(null);
  goodThreshold = input.required<number>();
  mediumThreshold = input.required<number>();

  presenceData = computed((): SharePresenceWithColleaguesEvaluatorData | null => {
    const currentMonthData = this.currentMonthEvaluation()?.data;
    return (currentMonthData as SharePresenceWithColleaguesEvaluatorData | undefined) ?? null;
  });

  sectionsData = computed<SharePresenceSectionWithProgress[]>(() => {
    const data = this.presenceData();
    if (!data) {
      return [];
    }

    return [
      data.current_month_to_date
        ? this.buildSection('Este mes (en curso)', data.current_month_to_date, {
            progress_bar_tooltip:
              'Muestra solo la presencialidad ya cumplida: horas de coincidencia registradas hasta hoy respecto al total de horas posibles de los días ya trabajados.',
          })
        : null,
      data.next_month_forecast
        ? this.buildSection(`Previsión (${data.next_month_forecast.month_label})`, data.next_month_forecast, { check_warning: true })
        : null,
    ].filter((section): section is SharePresenceSectionWithProgress => section !== null);
  });

  openDayDetailsDialog(): void {
    const presenceData = this.presenceData();
    if (!presenceData) {
      return;
    }
    this.dialog.open(
      SharePresenceWithColleaguesDayDetailsDialogComponent,
      createKpiDetailsDialogConfig({
        presenceData,
        optimal_threshold: this.goodThreshold(),
        acceptable_threshold: this.mediumThreshold(),
      })
    );
  }

  private buildSection(
    name: string,
    period: { hours_coincided: number; max_hours_possible: number },
    options: { check_warning?: boolean; progress_bar_tooltip?: string | null } = {}
  ): SharePresenceSectionWithProgress {
    const check_warning = options.check_warning ?? false;
    const percentage = period.max_hours_possible === 0 ? 0 : (period.hours_coincided / period.max_hours_possible) * 100;
    const good = this.goodThreshold();
    const medium = this.mediumThreshold();

    const evaluation =
      percentage >= good
        ? ProductionKeyPerformanceIndicatorEvaluationEnum.Good
        : percentage >= medium
          ? ProductionKeyPerformanceIndicatorEvaluationEnum.Medium
          : ProductionKeyPerformanceIndicatorEvaluationEnum.Poor;

    return {
      name,
      done_hours: period.hours_coincided,
      expected_hours: period.max_hours_possible,
      percentage,
      progressBarColor: getKpiEvaluationStyle(evaluation).color,
      isWarning: check_warning && percentage < good,
      progressBarTooltip: options.progress_bar_tooltip ?? null,
    };
  }
}
